import type { CharacterStyle } from '../core/characters';
import { buildFigure, emptyLimbState, secondaryFromVelocity, type LimbState } from '../render/figure';
import { characterGroups, grippingHandGroups } from '../render/characterPaint';
import { placeWeapon, weaponLook } from '../render/weaponShapes';
import { PRIM_CAPSULE, PRIM_DISK, PRIM_ROUNDED_BOX, type Primitive } from '../render/sdf/primitives';
import { WEAPON_BY_ID } from '../sim/weapons/defs';
import { heldWeaponAnchor } from '../sim/weapons/geometry';

const NS = 'http://www.w3.org/2000/svg';
export type PreviewPose = 'idle' | 'run' | 'jump' | 'duck' | 'attack' | 'guard' | 'victory';
export type PreviewOptions = { weapon?: string; pose?: PreviewPose; animated?: boolean; playing?: boolean; reducedMotion?: boolean; ready?: boolean; facing?: number; aim?: number };
export type CharacterPreview = SVGSVGElement & { update: (style: CharacterStyle, options: PreviewOptions) => void };

function layer(primitives: Primitive[], color: string): SVGGElement {
  const g = document.createElementNS(NS, 'g');
  g.setAttribute('fill', color); g.setAttribute('transform', 'scale(1 -1)');
  for (const p of primitives) {
    const el = document.createElementNS(NS, p.kind === PRIM_DISK ? 'circle' : p.kind === PRIM_CAPSULE ? 'line' : p.kind === PRIM_ROUNDED_BOX ? 'rect' : 'polygon');
    const values = p.kind === PRIM_DISK ? { cx: p.ax, cy: p.ay, r: p.r }
      : p.kind === PRIM_CAPSULE ? { x1: p.ax, y1: p.ay, x2: p.bx, y2: p.by, stroke: color, 'stroke-width': p.r * 2, 'stroke-linecap': 'round' }
      : p.kind === PRIM_ROUNDED_BOX ? { x: p.ax - p.bx, y: p.ay - p.by, width: p.bx * 2, height: p.by * 2, rx: p.r }
      : { points: `${p.ax - p.bx},${p.ay} ${p.ax + p.bx},${p.ay} ${p.ax},${p.ay + p.by}` };
    for (const [name, value] of Object.entries(values)) if (value !== undefined) el.setAttribute(name, String(value));
    if (p.rot && p.kind !== PRIM_CAPSULE && p.kind !== PRIM_DISK) el.setAttribute('transform', `rotate(${p.rot * 180 / Math.PI} ${p.ax} ${p.ay})`);
    g.append(el);
  }
  return g;
}

/** Only the open dressing-room preview animates; its loop stops when the menu is removed. */
export function characterPreview(style: CharacterStyle, color: string, options: PreviewOptions = {}): CharacterPreview {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reducedMotion = () => !!options.reducedMotion || prefersReducedMotion;
  const playing = () => !!options.animated && options.playing !== false && !reducedMotion();
  const svg = document.createElementNS(NS, 'svg') as CharacterPreview;
  svg.classList.add('character-preview');
  svg.setAttribute('viewBox', '-1.1 -1.25 3.3 2.5');
  svg.setAttribute('role', 'img');
  let secondary: LimbState = emptyLimbState(), last = 0, elapsed = 0, frameId: number | null = null;
  const stillTime: Record<PreviewPose, number> = { idle: 0, run: 0.2, jump: 0.32, duck: 0, attack: 0.24, guard: 0, victory: 0 };
  const draw = (dt: number) => {
    elapsed += dt;
    const pose = options.pose ?? 'idle';
    const facing = options.facing ?? 1, baseAim = options.aim ?? 0.08;
    const moving = pose === 'run', jumpTime = elapsed % 1.85;
    const jumping = pose === 'jump' && jumpTime < 1.15;
    const vy = jumping ? 10 - jumpTime * 17 : 0;
    const vx = moving ? facing * 6 : 0;
    const weapon = options.weapon === 'none' ? undefined : WEAPON_BY_ID.get(options.weapon ?? 'pistol')?.def;
    const attackTime = elapsed % 1.1;
    const attacking = pose === 'attack';
    const throwable = !!weapon?.throwProfile?.attackThrows || weapon?.projectile.kind === 'return-disc';
    const melee = weapon?.category === 'melee' && !throwable;
    const swing = !attacking || !melee ? 0 : attackTime < 0.16 ? attackTime / 0.16
      : attackTime < 0.38 ? 1 - (attackTime - 0.16) / 0.22 * 1.65
        : attackTime < 0.7 ? -0.65 * (1 - (attackTime - 0.38) / 0.32) : 0;
    const aim = baseAim + swing;
    const aimX = Math.cos(aim) * facing, aimY = Math.sin(aim);
    const released = attacking && throwable && attackTime >= 0.22 && attackTime < 0.88;
    if (!secondary.torso.live && dt === 0) {
      // A still Run or Jump shows the action itself, even when animation is disabled.
      secondary.runPhase = moving ? Math.PI : 0;
      secondary.runWeight = moving ? 1 : 0;
      secondary.hipSway = vx * 0.05;
      secondary.shoulderSway = -vx * 0.032 + vy * 0.016;
      secondary.lastVx = vx; secondary.lastVy = vy; secondary.wasGrounded = !jumping;
    }
    secondary = secondaryFromVelocity(secondary, vx, vy, !jumping, dt);
    const fig = buildFigure({ x: 0, y: jumping ? Math.sin(jumpTime / 1.15 * Math.PI) * 0.22 : 0, facing, ducking: pose === 'duck',
      grounded: !jumping, wallSliding: false, vx, vy, aimX, aimY,
      punching: attacking && ((!weapon && attackTime >= 0.15 && attackTime < 0.38) || released),
      windingUp: attacking && (!weapon || throwable) && attackTime < 0.15,
      recoil: attacking && weapon && !melee && !throwable && attackTime >= 0.16 ? Math.max(0, 1 - (attackTime - 0.16) / 0.25) : 0,
      blocking: pose === 'guard', dead: false, phase: elapsed,
      personality: style.personality, celebrating: options.ready || pose === 'victory', reducedMotion: reducedMotion(),
      weapon: weapon && !released ? { length: weapon.shape.length, twoHanded: weapon.twoHanded, kind: weapon.shape.kind, category: weapon.category } : null,
    }, secondary);
    const paint = characterGroups(fig, color, style);
    const groups = paint.map((group, index) => {
      const element = layer(group.primitives, group.color);
      element.dataset.characterLayer = String(index);
      return element;
    });
    if (weapon) {
      const look = weaponLook(weapon.shape.kind, weapon.shape.length);
      let a = fig.weaponAnchor;
      if (released) {
        const progress = (attackTime - 0.22) / 0.66;
        const travel = Math.sin(progress * Math.PI) * 0.75;
        const origin = heldWeaponAnchor({ x: 0, y: 0, aimX, aimY, ducking: false, twoHanded: weapon.twoHanded });
        a = { ...origin, x: origin.x + aimX * travel, y: origin.y + aimY * travel,
          angle: origin.angle + progress * Math.PI * 4 * facing };
      }
      groups.push(layer(placeWeapon(look.body, a.x, a.y, a.angle, a.flip), '#23252b'), layer(placeWeapon(look.accent, a.x, a.y, a.angle, a.flip), look.accentColor));
      if (!released) groups.push(...grippingHandGroups(fig, color, style).map((g) => layer(g.primitives, g.color)));
    }
    svg.replaceChildren(...groups);
  };
  const schedule = () => {
    if (!playing()) {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = null; last = 0;
      return;
    }
    if (frameId !== null) return;
    const frame = (now: number) => {
      frameId = null;
      if (!svg.isConnected || !playing()) { last = 0; return; }
      if (!last || now - last >= 1000 / 24) { draw(last ? Math.min(0.05, (now - last) / 1000) : 1 / 24); last = now; }
      frameId = requestAnimationFrame(frame);
    };
    frameId = requestAnimationFrame(frame);
  };
  svg.update = (nextStyle, nextOptions) => {
    const changed = !secondary.torso.live || nextStyle.personality !== style.personality
      || (['pose', 'weapon', 'facing', 'aim'] as const).some((key) => nextOptions[key] !== undefined && nextOptions[key] !== options[key]);
    style = nextStyle; options = { ...options, ...nextOptions };
    if (changed) {
      const weapon = WEAPON_BY_ID.get(options.weapon ?? 'pistol')?.def;
      const throwing = options.pose === 'attack' && (weapon?.throwProfile?.attackThrows || weapon?.projectile.kind === 'return-disc');
      elapsed = playing() ? 0 : throwing ? 0.46 : stillTime[options.pose ?? 'idle'];
      secondary = emptyLimbState(); last = 0;
    }
    const unarmed = options.weapon === 'none';
    const wide = options.pose === 'attack' && !unarmed;
    svg.setAttribute('viewBox', unarmed ? '-1.05 -1.25 2.1 2.5' : wide
      ? (options.facing ?? 1) < 0 ? '-2.35 -1.65 3.7 3.1' : '-1.35 -1.65 3.7 3.1'
      : (options.facing ?? 1) < 0 ? '-2.2 -1.25 3.3 2.5' : '-1.1 -1.25 3.3 2.5');
    svg.setAttribute('aria-label', `${style.personality} fighter, ${style.skin ?? 'classic'} skin, ${style.accessory}, ${style.marking} markings`);
    svg.dataset.personality = style.personality;
    svg.dataset.accessory = style.accessory;
    svg.dataset.marking = style.marking;
    svg.dataset.skin = style.skin ?? 'classic';
    svg.dataset.facing = String(options.facing ?? 1);
    svg.dataset.pose = options.pose ?? 'idle';
    svg.dataset.playing = String(playing());
    svg.dataset.reducedMotion = String(reducedMotion());
    draw(0);
    schedule();
  };
  svg.update(style, options);
  return svg;
}
